import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { humanError } from "../../shared/api/client";
import { Disclosure } from "../../shared/ui/Disclosure";
import { listAccounts, restoreAccount } from "./api";

interface ClosedAccountsProps {
  bookID: string;
  onNotify: (message: string) => void;
}

// Archiving hides an account and keeps every posting it ever carried. Without
// somewhere to see the hidden ones there is nothing to reopen, so this is the
// other half of archiving rather than a separate feature — and it stays behind
// a disclosure, because a closed account is not something anyone needs in
// front of them every day.
export function ClosedAccounts({ bookID, onNotify }: ClosedAccountsProps) {
  const queryClient = useQueryClient();
  const closed = useQuery({
    queryKey: ["closed-accounts", bookID],
    queryFn: async () => (await listAccounts(bookID, true)).accounts.filter((account) => account.archived),
  });
  const reopen = useMutation({
    mutationFn: (accountID: string) => restoreAccount(bookID, accountID),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["closed-accounts"] });
      onNotify(`${result.account.name} is open again, with the history it kept.`);
    },
  });

  if (closed.data && closed.data.length === 0) return null;

  return (
    <section className="section-block accounts-panel" aria-labelledby="closed-accounts-title">
      <h2 id="closed-accounts-title" className="visually-hidden">Closed accounts</h2>
      <Disclosure
        title={`Closed accounts${closed.data ? ` (${closed.data.length})` : ""}`}
        hint="hidden, with every posting they carried"
      >
        <p className="panel-note">
          Archiving hides an account and keeps every posting it carried. Reopening brings it back with that history and
          nothing else changes.
        </p>
        {closed.isPending ? <p className="panel-note">Looking…</p> : null}
        {closed.isError ? <p className="form-error" role="alert">{humanError(closed.error)}</p> : null}
        <ul className="pick-list">
          {(closed.data ?? []).map((account) => (
            <li className="pick-row" key={account.id}>
              <span className="pick-main readonly">
                <span className="pick-copy">
                  <strong>{account.name}</strong>
                  <small>{account.currency}{account.displayLabel ? ` · ${account.displayLabel}` : ""}</small>
                </span>
                <button className="chip" type="button" disabled={reopen.isPending} onClick={() => reopen.mutate(account.id)}>
                  Reopen
                </button>
              </span>
            </li>
          ))}
        </ul>
        {reopen.error ? <p className="form-error" role="alert">{humanError(reopen.error)}</p> : null}
      </Disclosure>
    </section>
  );
}
